import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const Wrapper = styled.div`
  width: 100%;
`;

const SectionTitle = styled.h2`
  font-size: 0.85rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 1.25rem 0;
  display: flex;
  align-items: center;
  gap: 10px;
  letter-spacing: 0.02em;

  &::before {
    content: "";
    width: 3px;
    height: 14px;
    background: #0c4d96;
    border-radius: 2px;
  }
`;

const Card = styled.a`
  display: block;
  background: linear-gradient(135deg, #0c4d96 0%, #1a5fa8 100%);
  border-radius: 12px;
  padding: 1.5rem 1.25rem;
  position: relative;
  overflow: hidden;
  text-decoration: none;
  color: white;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  box-shadow: 0 2px 8px rgba(12, 77, 150, 0.15);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(12, 77, 150, 0.25);
  }

  &::before {
    content: "";
    position: absolute;
    top: -60%;
    right: -30%;
    width: 80%;
    height: 180%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.12) 0%,
      transparent 60%
    );
    pointer-events: none;
  }
`;

const IconRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.85rem;
  position: relative;
`;

const IconBox = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
    stroke: white;
    fill: none;
    stroke-width: 2;
  }
`;

const Tag = styled.span`
  font-size: 0.6rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  padding: 3px 8px;
  border-radius: 4px;
  letter-spacing: 0.05em;
`;

const Title = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 0.4rem;
  position: relative;
  letter-spacing: -0.01em;
`;

const Description = styled.p`
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.85);
  margin: 0 0 1.1rem 0;
  line-height: 1.55;
  position: relative;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.1rem 0;
  position: relative;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 0.35rem;
  line-height: 1.3;

  &::before {
    content: "";
    flex-shrink: 0;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.7);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const CtaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.85rem;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  position: relative;
`;

const CtaText = styled.span`
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.01em;
`;

const ArrowCircle = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.25s ease;

  ${Card}:hover & {
    transform: translateX(3px);
  }

  svg {
    width: 12px;
    height: 12px;
    stroke: #0c4d96;
    fill: none;
    stroke-width: 2.5;
  }
`;

const CalcIcon = () => (
  <svg viewBox="0 0 24 24">
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <line x1="8" y1="6" x2="16" y2="6" />
    <line x1="8" y1="10" x2="16" y2="10" />
    <line x1="8" y1="14" x2="10" y2="14" />
    <line x1="8" y1="18" x2="10" y2="18" />
    <line x1="14" y1="14" x2="16" y2="14" />
    <line x1="14" y1="18" x2="16" y2="18" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

export default function SalaryCalculatorCTA() {
  const { t } = useTranslation();

  return (
    <Wrapper>
      <SectionTitle>{t("salary_calculator.title")}</SectionTitle>
      <Card href="/recruitment/salary">
        <IconRow>
          <IconBox>
            <CalcIcon />
          </IconBox>
          <Tag>{t("salary_cta.tag")}</Tag>
        </IconRow>
        <Title>{t("salary_cta.title")}</Title>
        <Description>{t("salary_cta.description")}</Description>
        <FeaturesList>
          <FeatureItem>{t("salary_cta.feature1")}</FeatureItem>
          <FeatureItem>{t("salary_cta.feature2")}</FeatureItem>
          <FeatureItem>{t("salary_cta.feature3")}</FeatureItem>
        </FeaturesList>
        <CtaRow>
          <CtaText>{t("salary_cta.button")}</CtaText>
          <ArrowCircle>
            <ArrowIcon />
          </ArrowCircle>
        </CtaRow>
      </Card>
    </Wrapper>
  );
}
